import { motion } from "framer-motion";
import { Play, Pause, Mic } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type Turn =
  | { from: "bot" | "me"; kind: "text"; text: string | JSX.Element; time: string }
  | {
      from: "bot" | "me";
      kind: "voice";
      duration: string;
      time: string;
      waveform: number[];
      src: string;
    }
  | { from: "bot" | "me"; kind: "photo"; caption?: string; time: string };

const messages: Turn[] = [
  {
    from: "bot",
    kind: "text",
    text: (
      <>
        Morning! Coffee first or <i>die Arbeit</i>{" "}
        <span className="text-paper/50">(the work, "ar-bite")</span>? I'm guessing coffee.
      </>
    ),
    time: "08:42",
  },
  { from: "me", kind: "text", text: "coffee, obviously", time: "08:44" },
  {
    from: "bot",
    kind: "text",
    text: (
      <>
        A woman of <i>der Verstand</i>{" "}
        <span className="text-paper/50">(the sense, "fair-shtant")</span>. Any plans tonight, or is it a{" "}
        <i>Feierabend</i> <span className="text-paper/50">(cozy end-of-workday)</span> on the sofa?
      </>
    ),
    time: "08:44",
  },
  {
    from: "me",
    kind: "voice",
    duration: "0:07",
    time: "08:45",
    waveform: [3, 6, 9, 12, 8, 14, 10, 16, 11, 7, 13, 9, 15, 11, 8, 12, 6, 10, 14, 7, 11, 9, 13, 8, 10],
    src: "/sandwich-user.mp3",
  },
  {
    from: "bot",
    kind: "voice",
    duration: "0:11",
    time: "08:45",
    waveform: [4, 7, 11, 15, 9, 13, 17, 10, 14, 8, 12, 16, 11, 7, 13, 9, 14, 10, 6, 11, 15, 8, 12, 10, 14, 7, 11, 9, 13, 6],
    src: "/sandwich-bot.mp3",
  },
  {
    from: "me",
    kind: "photo",
    caption: "saw this on a hike — what does it mean?",
    time: "11:02",
  },
  {
    // Real reply captured from the deployed bot — verbatim, not curated.
    from: "bot",
    kind: "text",
    text: (
      <>
        "<i>Durchgang auf eigene Gefahr!</i>"{" "}
        <span className="text-paper/50">(Passage at own risk)</span>. That is{" "}
        <i>eine</i>{" "}
        <span className="text-paper/50">(a, "ay-ne")</span>{" "}
        <i>gefährliche</i>{" "}
        <span className="text-paper/50">(dangerous, "ge-fer-li-she")</span>{" "}
        <i>Warnung</i>{" "}
        <span className="text-paper/50">(warning, "var-noong")</span>{" "}
        for anyone walking there.
      </>
    ),
    time: "11:02",
  },
];

function Waveform({
  bars, active, progress,
}: { bars: number[]; active: boolean; progress: number }) {
  // `progress` is 0..1 of playback. Bars left of the cursor render
  // solid; right side dim. Same shape used by Telegram + WhatsApp.
  const cursorBar = progress * bars.length;
  return (
    <div className="flex items-center gap-[2px] h-5">
      {bars.map((h, i) => {
        const played = i < cursorBar;
        const baseColor = active ? "bg-paper" : "bg-paper/85";
        const dimColor = active ? "bg-paper/35" : "bg-paper/30";
        return (
          <span
            key={i}
            className={`w-[2px] rounded-full transition-colors ${
              played ? baseColor : dimColor
            }`}
            style={{ height: `${h}px` }}
          />
        );
      })}
    </div>
  );
}

function PhotoBubble({ turn }: { turn: Extract<Turn, { kind: "photo" }> }) {
  const isMe = turn.from === "me";
  return (
    <div
      className={`max-w-[85%] rounded-[14px] overflow-hidden ${
        isMe ? "bg-terracotta text-paper rounded-br-sm" : "bg-ink-card-soft text-paper/90 rounded-bl-sm"
      }`}
    >
      <img
        src="/landing-sign.png"
        alt="A German roadside sign reading: Durchgang auf eigene Gefahr! — Stierweide"
        loading="lazy"
        className="block w-[240px] h-auto mx-auto mt-2 rounded-[8px]"
      />
      {turn.caption ? (
        <div className="px-3.5 py-2 text-[0.85rem] leading-relaxed">
          {turn.caption}
          <span className={`ml-2 text-[0.65rem] ${isMe ? "text-paper/70" : "text-paper/40"}`}>
            {turn.time}
          </span>
        </div>
      ) : (
        <div className={`px-3 py-1.5 text-right text-[0.65rem] ${isMe ? "text-paper/70" : "text-paper/40"}`}>
          {turn.time}
        </div>
      )}
    </div>
  );
}

// Module-level audio singleton: playing one bubble pauses any other
// that's currently playing. Mirrors how Telegram + most messengers
// behave — no two voice notes blast at once.
const _activeAudio: { audio: HTMLAudioElement | null; setOff: (() => void) | null } = {
  audio: null,
  setOff: null,
};

function VoiceBubble({ turn }: { turn: Extract<Turn, { kind: "voice" }> }) {
  const isMe = turn.from === "me";
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  // Lazy-instantiate so the file is only fetched on first play —
  // keeps the landing page fast for viewers who never click.
  function getAudio(): HTMLAudioElement {
    if (!audioRef.current) {
      const a = new Audio(turn.src);
      a.preload = "metadata";
      a.addEventListener("timeupdate", () => {
        if (a.duration > 0) setProgress(a.currentTime / a.duration);
      });
      a.addEventListener("ended", () => {
        setPlaying(false);
        setProgress(0);
      });
      audioRef.current = a;
    }
    return audioRef.current;
  }

  // If a different bubble starts, stop this one.
  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  function toggle() {
    const a = getAudio();
    if (playing) {
      a.pause();
      setPlaying(false);
      _activeAudio.audio = null;
      _activeAudio.setOff = null;
      return;
    }
    // Stop whatever else is playing first.
    if (_activeAudio.audio && _activeAudio.audio !== a) {
      _activeAudio.audio.pause();
      _activeAudio.setOff?.();
    }
    a.play().then(() => {
      setPlaying(true);
      _activeAudio.audio = a;
      _activeAudio.setOff = () => setPlaying(false);
    }).catch(() => {
      // Autoplay blocked / asset missing — quietly fail; the icon
      // stays Play and the user can try again.
    });
  }

  return (
    <div
      className={`flex items-center gap-3 min-w-[220px] max-w-[85%] px-3 py-2.5 rounded-[14px] ${
        isMe ? "bg-terracotta text-paper rounded-br-sm" : "bg-ink-card-soft text-paper/90 rounded-bl-sm"
      }`}
    >
      <button
        onClick={toggle}
        className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-transform active:scale-95 ${
          isMe ? "bg-paper text-terracotta" : "bg-paper text-ink"
        }`}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? (
          <Pause size={14} fill="currentColor" />
        ) : (
          <Play size={14} fill="currentColor" className="translate-x-[1px]" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <Waveform bars={turn.waveform} active={isMe} progress={progress} />
        <div className={`flex items-center justify-between mt-1 text-[0.65rem] ${isMe ? "text-paper/70" : "text-paper/45"}`}>
          <span className="tabular-nums">{turn.duration}</span>
          <span className="flex items-center gap-1.5">
            <Mic size={9} />
            <span className="tabular-nums">{turn.time}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ChatMock({ className = "" }: { className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`bg-paper rounded-[14px] shadow-float w-full max-w-[440px] overflow-hidden relative z-10 ${className}`}
    >
      {/* Light header strip */}
      <div className="flex items-center gap-3.5 px-5 py-4 bg-paper border-b border-[rgba(42,36,26,0.12)]">
        <div className="w-11 h-11 shrink-0 rounded-full bg-paper text-ink flex items-center justify-center font-serif leading-none ring-1 ring-[rgba(42,36,26,0.12)] overflow-hidden p-1.5">
          <svg viewBox="0 0 70 90" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden>
            <path
              d="M 12 26 C 12 12, 26 6, 42 8 C 58 10, 64 22, 60 38 C 56 54, 50 60, 46 70 C 44 78, 38 84, 30 84 C 22 84, 18 76, 18 68 C 18 54, 12 40, 12 26 Z"
              stroke="#17171A" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"
            />
            <g transform="translate(20 20) scale(1.5) translate(-2 -3)">
              <path
                d="M3,13.5793719 C9.98914658,5.83454059 14.2442185,2.78592627 15.7652158,4.43352892 C18.0467117,6.90493289 7.55581053,16.1455344 9.47834357,17.954063 C11.4008766,19.7625917 16.9959382,11.5719148 19.0578414,12.4109285 C21.1197445,13.2499421 16.1152903,18.1722847 17.4055985,18.9997829 C18.2658039,19.5514483 19.3191667,19.0606734 20.5656867,17.527458"
                stroke="#B8472C" strokeWidth="2.67" strokeLinecap="round" strokeLinejoin="round" fill="none"
              />
            </g>
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-ink font-semibold text-[1.05rem] leading-tight">ilmli</div>
          <div className="text-ink-soft text-xs mt-0.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
            bot · online
          </div>
        </div>
      </div>

      <div className="p-5 md:p-6 space-y-3">
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + i * 0.18, duration: 0.4 }}
            className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}
          >
            {m.kind === "voice" ? (
              <VoiceBubble turn={m} />
            ) : m.kind === "photo" ? (
              <PhotoBubble turn={m} />
            ) : (
              <div
                className={`max-w-[85%] px-3.5 py-2.5 rounded-[14px] text-[0.88rem] leading-relaxed ${
                  m.from === "me"
                    ? "bg-terracotta text-paper rounded-br-sm"
                    : "bg-ink-card-soft text-paper/90 rounded-bl-sm"
                }`}
              >
                {m.text}
                <span className={`ml-2 text-[0.65rem] ${m.from === "me" ? "text-paper/70" : "text-paper/40"}`}>
                  {m.time}
                </span>
              </div>
            )}
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 0.3 }}
          className="flex justify-start"
        >
          <div className="bg-ink-card-soft rounded-[14px] rounded-bl-sm px-3.5 py-3 flex gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-paper/60 animate-dots" />
            <span className="w-1.5 h-1.5 rounded-full bg-paper/60 animate-dots" style={{ animationDelay: "0.15s" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-paper/60 animate-dots" style={{ animationDelay: "0.3s" }} />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
